<script setup lang="ts">
/**
 * One reported figure, set the way a disclosure is read.
 *
 * The figure large, its unit beside it rather than inside it, and the
 * comparative period underneath at the same weight as the label. That order is
 * not decoration: a reader looking for a number finds it, and a reader
 * checking a movement finds both periods without hunting.
 *
 * The method sits under a rule, quiet and small. It is the part an assurance
 * provider reads and everybody else skips, so it has to be there and it has to
 * not compete with the figure.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import type { DatapointSection } from '@/domain/section'

const { t } = useI18n()

const props = defineProps<{ section: DatapointSection }>()

const label = computed(() => props.section.label.trim() || props.section.title)

/** Only where there is one. A blank comparative row says nothing twice. */
const hasComparative = computed(() => props.section.comparative.trim() !== '')
</script>

<template>
  <div class="datapoint">
    <p class="datapoint__label">{{ label || t('canvas.defaults.datapoint') }}</p>

    <p class="datapoint__figure">
      <span class="datapoint__value">{{ section.value || '—' }}</span>
      <span v-if="section.unit" class="datapoint__unit">{{ section.unit }}</span>
      <span v-if="section.period" class="datapoint__period">{{ section.period }}</span>
    </p>

    <p v-if="hasComparative" class="datapoint__comparative">
      <span class="datapoint__comparative-value">{{ section.comparative }}</span>
      <span v-if="section.unit" class="datapoint__unit">{{ section.unit }}</span>
      <span v-if="section.comparativePeriod" class="datapoint__period">
        {{ section.comparativePeriod }}
      </span>
    </p>

    <p v-if="section.method" class="datapoint__method">
      <span class="datapoint__method-label">{{ t('inspector.datapoint.method') }}</span>
      {{ section.method }}
    </p>
  </div>
</template>

<style scoped>
.datapoint {
  margin: 3mm 0 4mm;
  padding-left: 3mm;
  border-left: 2px solid var(--page-accent);
}

.datapoint__label {
  margin: 0 0 1.5mm;
  color: var(--paper-ink-soft);
  font-size: 9pt;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.datapoint__figure {
  display: flex;
  align-items: baseline;
  gap: 2mm;
  margin: 0;
}

.datapoint__value {
  font-size: 22pt;
  font-weight: 600;
  line-height: 1.1;
  font-variant-numeric: tabular-nums lining-nums;
}

/* Beside the figure, never inside it. A unit typed into the value is a value
   nothing downstream can read as a number. */
.datapoint__unit {
  color: var(--paper-ink-soft);
  font-size: 10pt;
}

.datapoint__period {
  margin-left: auto;
  color: var(--paper-ink-soft);
  font-size: 9pt;
}

.datapoint__comparative {
  display: flex;
  align-items: baseline;
  gap: 2mm;
  margin: 1.5mm 0 0;
  color: var(--paper-ink-soft);
  font-size: 11pt;
}

.datapoint__comparative-value {
  font-variant-numeric: tabular-nums lining-nums;
}

/* Under a rule and small: the part an assurance provider reads and everybody
   else skips, so it has to be there without competing with the figure. */
.datapoint__method {
  margin: 2.5mm 0 0;
  padding-top: 1.5mm;
  border-top: 1px solid var(--paper-rule);
  color: var(--paper-ink-soft);
  font-size: 8.5pt;
  line-height: 1.5;
  text-align: left;
}

.datapoint__method-label {
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
</style>
